import { useLanguage } from '../context/LanguageContext';
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Package, Clock, CreditCard } from 'lucide-react';

export default function PolitiqueRetour() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-[#00aaff] mb-6 transition-colors">
          <ArrowLeft size={18} />
          <span className="text-sm">Retour à l'accueil</span>
        </Link>

        <h1 className="text-3xl md:text-5xl font-black text-gray-900 mb-8">Politique de Retour et Remboursement</h1>

        <div className="prose prose-gray max-w-none space-y-8">
          {/* Intro */}
          <section>
            <p className="text-gray-700 leading-relaxed">
              Chez <strong>Joey Cyan</strong>, nous souhaitons que vous soyez pleinement satisfait de votre achat. Si pour une raison quelconque vous n'êtes pas satisfait de votre commande, nous acceptons les retours conformément à la réglementation française en vigueur.
            </p>
          </section>

          {/* Délai de rétractation */}
          <section className="bg-[#00aaff]/5 border-l-4 border-[#00aaff] p-6 rounded-r-xl">
            <div className="flex items-start gap-4">
              <Clock size={32} className="text-[#00aaff] shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Délai de rétractation : 14 jours</h2>
                <p className="text-gray-700 leading-relaxed">
                  Conformément à l'article L.221-18 du Code de la consommation, vous disposez d'un délai de <strong>14 jours calendaires</strong> à compter de la réception de votre commande pour nous retourner un produit, sans avoir à justifier de motifs ni à payer de pénalités.
                </p>
              </div>
            </div>
          </section>

          {/* Conditions de retour */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Conditions de retour</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Pour être accepté, votre retour doit respecter les conditions suivantes :
            </p>
            <ul className="list-none space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <span className="text-[#00aaff] font-bold shrink-0">✓</span>
                <span>Le produit doit être dans son <strong>emballage d'origine</strong>, non ouvert et non utilisé</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#00aaff] font-bold shrink-0">✓</span>
                <span>Le produit doit être <strong>complet</strong> avec tous ses accessoires (câbles, étui, notice, etc.)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#00aaff] font-bold shrink-0">✓</span>
                <span>Le produit ne doit présenter <strong>aucun signe d'usure</strong> ou de détérioration</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#00aaff] font-bold shrink-0">✓</span>
                <span>Les <strong>films de protection</strong> et étiquettes ne doivent pas avoir été retirés</span>
              </li>
            </ul>

            <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-amber-900 text-sm leading-relaxed">
                <strong>⚠️ Important :</strong> Pour des raisons d'hygiène et de santé, les produits portés ou essayés de manière prolongée ne pourront pas être repris.
              </p>
            </div>
          </section>

          {/* Frais de retour */}
          <section className="bg-gray-50 border-2 border-gray-200 p-6 rounded-xl">
            <div className="flex items-start gap-4">
              <CreditCard size={32} className="text-gray-700 shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Frais de retour</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  ⚠️ <strong>Les frais de retour sont à la charge du client</strong>, sauf en cas de produit défectueux ou d'erreur de notre part.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Nous vous recommandons d'utiliser un service de livraison avec suivi pour retourner votre colis. Joey Cyan ne pourra être tenue responsable d'un colis perdu lors du retour.
                </p>
              </div>
            </div>
          </section>

          {/* Procédure de retour */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Comment effectuer un retour ?</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Pour retourner un produit, suivez ces étapes :
            </p>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#00aaff] text-white font-bold shrink-0">1</div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Contactez-nous</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Envoyez un email à <strong>contact@joeycyan.com</strong> en indiquant :
                  </p>
                  <ul className="list-disc list-inside mt-2 text-gray-700 ml-4">
                    <li>Votre numéro de commande</li>
                    <li>Le(s) produit(s) à retourner</li>
                    <li>Le motif du retour</li>
                  </ul>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#00aaff] text-white font-bold shrink-0">2</div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Recevez votre confirmation</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Nous vous enverrons une confirmation par email avec l'adresse de retour et les instructions détaillées.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#00aaff] text-white font-bold shrink-0">3</div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Préparez votre colis</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Emballez soigneusement le produit dans son emballage d'origine avec tous les accessoires. Incluez une copie de votre facture.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#00aaff] text-white font-bold shrink-0">4</div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Expédiez le colis</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Envoyez le colis à l'adresse indiquée. Conservez votre preuve d'expédition et le numéro de suivi.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-blue-900 text-sm font-semibold mb-2">📦 Adresse de retour :</p>
              <p className="text-blue-900 text-sm">
                Joey Cyan - Service Retours<br />
                33 Avenue Philippe Auguste<br />
                75011 PARIS<br />
                France
              </p>
            </div>
          </section>

          {/* Remboursement */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Remboursement</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Une fois votre retour reçu et inspecté, nous vous enverrons un email pour vous notifier de la réception. Si le retour est accepté, nous procéderons au remboursement dans un délai de <strong>14 jours</strong> suivant la réception du produit.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Le remboursement sera effectué selon le même mode de paiement utilisé lors de l'achat.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Montant remboursé :</strong> Le prix du produit TTC. Les frais de livraison initiaux ne sont pas remboursés (sauf en cas de produit défectueux ou d'erreur de notre part).
            </p>
          </section>

          {/* Échanges */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Échanges</h2>
            <p className="text-gray-700 leading-relaxed">
              Nous n'effectuons pas d'échanges directs. Si vous souhaitez un modèle ou une couleur différente, nous vous invitons à retourner le produit pour remboursement et à passer une nouvelle commande sur notre site.
            </p>
          </section>

          {/* Produits défectueux */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Produits défectueux ou endommagés</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Si vous recevez un produit défectueux, endommagé ou non conforme à votre commande, veuillez nous contacter immédiatement à <strong>contact@joeycyan.com</strong> avec :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Votre numéro de commande</li>
              <li>Une description du problème</li>
              <li>Des photos du produit et de son emballage</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Dans ce cas, nous prendrons en charge les <strong>frais de retour</strong> et vous proposerons un remplacement ou un remboursement intégral (incluant les frais de livraison).
            </p>
          </section>

          {/* Garantie */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Garantie légale</h2>
            <p className="text-gray-700 leading-relaxed">
              Au-delà du délai de rétractation de 14 jours, tous nos produits bénéficient de la garantie légale de conformité de 2 ans conformément au Code de la consommation. Pour plus d'informations, consultez nos <Link to="/cgv" className="text-[#00aaff] hover:underline">Conditions Générales de Vente</Link>.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-gray-50 p-6 rounded-xl">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions ?</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Notre service client est à votre disposition pour toute question concernant les retours et remboursements :
            </p>
            <ul className="list-none space-y-2 text-gray-700 ml-0">
              <li><strong>Email :</strong> contact@joeycyan.com</li>
              <li><strong>Adresse :</strong> 33 Avenue Philippe Auguste, 75011 Paris</li>
            </ul>
          </section>
        </div>

        <p className="text-sm text-gray-400 mt-12">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
      </div>
    </div>
  );
}
